import React, { useEffect, useState } from 'react';
import { Icon } from '@plone/volto/components';
import closeIcon from '../../../icons/Close_circle_white.svg';
import { toPublicURL } from '@plone/volto/helpers';
import { useIntl } from 'react-intl';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { hasBlocksData } from '@plone/volto/helpers';
import { Container } from '@plone/components';
import config from '@plone/volto/registry';

const WARNING_BLOCK_STORAGE_KEY = 'warningBlock';
// for 5 min - testing
//const WARNING_BLOCK_TTL = 5 * 60 * 1000;
// for 3 hours
const WARNING_BLOCK_TTL = 3 * 60 * 60 * 1000;

const getWarningIdentifier = (data) => data?.UID || '';

const getStoredWarningState = (storageKey, warningId) => {
  const storedValue = localStorage.getItem(storageKey);

  if (!storedValue) {
    return false;
  }

  try {
    const parsedValue = JSON.parse(storedValue);
    const now = Date.now();

    if (
      !parsedValue?.closedAt ||
      !parsedValue?.expiresAt ||
      parsedValue.expiresAt <= now ||
      parsedValue.warningId !== warningId
    ) {
      localStorage.removeItem(storageKey);
      return false;
    }

    return true;
  } catch {
    localStorage.removeItem(storageKey);
    return false;
  }
};

const WarningBlock = () => {
  const [isClosed, setIsClosed] = useState(undefined);

  // content blocks
  const [data, setData] = useState([]); // State to hold blocks
  const [, setLoading] = useState(true); // State for loading indicator
  const [, setError] = useState(null); // State for error handling
  const intl = useIntl();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          toPublicURL(`/++api++/${intl.locale}/warning`),
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        setData(data || {});
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [intl.locale]);

  // end content blocks

  useEffect(() => {
    if (data.review_state === undefined) {
      // initial before data fetching
      return;
    } else if (data.review_state === 'published') {
      const warningId = getWarningIdentifier(data);
      const blockClosed = getStoredWarningState(
        WARNING_BLOCK_STORAGE_KEY,
        warningId,
      );
      setIsClosed(blockClosed);
    } else {
      // If unpublished, reset the isClosed state and clear localStorage
      setIsClosed(false);
      localStorage.removeItem(WARNING_BLOCK_STORAGE_KEY);
    }
  }, [data]); // Runs after data is fetched and review_state is available

  const handleClose = () => {
    const now = Date.now();
    const warningId = getWarningIdentifier(data);

    setIsClosed(true);
    localStorage.setItem(
      WARNING_BLOCK_STORAGE_KEY,
      JSON.stringify({
        closedAt: now,
        expiresAt: now + WARNING_BLOCK_TTL,
        warningId,
      }),
    );
  };

  const blockClassName =
    isClosed === undefined
      ? 'warning hidden'
      : isClosed
        ? 'warning closed'
        : 'warning';

  return (
    data.review_state === 'published' && (
      <Container className={blockClassName} role="banner">
        {!isClosed && (
          <div className="warning-wrapper fatal">
            <div className="warning-image">
              <img src={data.preview_image?.download || ''} alt="" />
            </div>
            <div className="warning-content">
              {hasBlocksData(data) && (
                <>
                  <RenderBlocks
                    content={data}
                    blocksConfig={config.blocks.blocksConfig}
                  />
                </>
              )}
            </div>

            <button className="close" onClick={handleClose}>
              <Icon name={closeIcon} size="24px" />
            </button>
          </div>
        )}
      </Container>
    )
  );
};

export default WarningBlock;
